import Image from "next/image";
import Link from "next/link";

type MasonryItem = {
  image?: string;
  title?: string;
  description?: string | null;
  link_type?: "url" | "page" | string;
  link_url?: string;
  link_text?: string;
  background_color?: string;
  background_image?: string;
  background_gradient?: "gradient-1" | "gradient-2" | "gradient-3" | "gradient-4" | string;
  size?: "small" | "medium" | "large" | "wide" | "tall" | string;
  col_span?: number | string;
};

type MasonryGridSectionProps = {
  heading?: string;
  items: MasonryItem[];
  add_background?: boolean;
  add_bottom_border?: boolean;
};

export default function MasonryGridSection({
  heading,
  items,
  add_background = false,
  add_bottom_border = false,
}: MasonryGridSectionProps) {
  const getColumnSpan = (item: MasonryItem) => {
    const parsedColSpan = Number(item.col_span);

    if ([1, 2, 3].includes(parsedColSpan)) {
      return parsedColSpan;
    }

    if (["large", "wide", "extra-large"].includes(item.size || "")) {
      return 3;
    }

    return 1;
  };

  const getSpanClass = (colSpan: number) => {
    const spanMap: Record<number, string> = {
      1: "md:col-span-1 lg:col-span-1",
      2: "md:col-span-2 lg:col-span-2",
      3: "md:col-span-2 lg:col-span-3",
    };

    return spanMap[colSpan] || spanMap[1];
  };

  // Map gradient presets to image files and text colors
  const getGradientConfig = (gradient?: string) => {
    const gradientMap: Record<string, { image: string; textColor: string }> = {
      "gradient-1": { image: "bg-gradasi-1.jpg", textColor: "#462859" },
      "gradient-2": { image: "bg-gradasi-2.jpg", textColor: "#FFF" },
      "gradient-3": { image: "bg-gradasi-3.jpg", textColor: "#266D6D" },
      "gradient-4": { image: "bg-gradasi-4.png", textColor: "#25215D" },
    };
    return gradientMap[gradient || ""] || null;
  };

  const renderItem = (item: MasonryItem, index: number) => {
    const spanClass = getSpanClass(getColumnSpan(item));
    const gradientConfig = getGradientConfig(item.background_gradient);
    
    // Determine background style and text color
    let hoverBackgroundStyle: React.CSSProperties | undefined;
    let textColor = "#000"; // Default black text
    
    if (gradientConfig) {
      hoverBackgroundStyle = {
        backgroundImage: `url(/images/${gradientConfig.image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };
      textColor = gradientConfig.textColor;
    } else if (item.background_image) {
      hoverBackgroundStyle = {
        backgroundImage: `url(/images/${item.background_image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      };
    } else if (item.background_color) {
      // For custom background colors
      textColor = "#FFF";
    }
    
    const content = (
      <div
        className={`
          bg-white shadow-sm
          rounded-2xl overflow-hidden relative
          group cursor-pointer
          transition-all duration-300 hover:scale-[1.02] hover:shadow-xl
          flex h-full flex-col
        `}
      >
        {/* Default white background */}
        <div className="absolute inset-0 bg-white transition-opacity duration-300 group-hover:opacity-0" />
        
        {/* Hover background - gradient/image/color */}
        <div 
          className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${item.background_color || ''}`}
          style={hoverBackgroundStyle}
        />
        
        {/* Image overlay if item has an image prop */}
        {item.image && (
          <div className="absolute inset-0 w-full h-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <Image
              src={item.image}
              alt={item.title || "Masonry item"}
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-black/20" />
          </div>
        )}

        {/* Content Overlay */}
        <div className="relative z-10 p-6 h-full flex flex-col justify-start">
          {item.title && (
            <h3 
              className="text-primary-700 group-hover:[color:var(--hover-text-color)] font-bold text-lg md:text-xl mb-2 transition-colors duration-300"
              style={{
                ['--hover-text-color' as string]: textColor
              } as React.CSSProperties}
            >
              {item.title}
            </h3>
          )}
          {item.description && (
            <div 
              className="text-black group-hover:[color:var(--hover-text-color)] text-[18px] transition-colors duration-300 opacity-90 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6"
              style={{
                ['--hover-text-color' as string]: textColor
              } as React.CSSProperties}
              dangerouslySetInnerHTML={{ __html: item.description }}
            />
          )}
          {item.link_text && (
            <span 
              className="text-black group-hover:[color:var(--hover-text-color)] font-semibold text-sm mt-3 inline-flex items-center group-hover:gap-2 transition-all duration-300"
              style={{
                ['--hover-text-color' as string]: textColor
              } as React.CSSProperties}
            >
              {item.link_text}
              <span className="opacity-0 group-hover:opacity-100 transition-opacity">→</span>
            </span>
          )}
        </div>
      </div>
    );

    // Wrap in Link if there's a link_url
    if (item.link_url) {
      const isExternal = item.link_url.startsWith("http");
      return (
        <div key={index} className={spanClass}>
          <Link
            href={item.link_url}
            target={isExternal ? "_blank" : undefined}
            rel={isExternal ? "noopener noreferrer" : undefined}
            className="block h-full"
          >
            {content}
          </Link>
        </div>
      );
    }

    return <div key={index} className={spanClass}>{content}</div>;
  };

  const content = (
    <>
      {heading && (
        <h2 className="text-3xl md:text-4xl font-bold text-primary-700 mb-8">
          {heading}
        </h2>
      )}
      <div
        className={`
          grid grid-cols-1 gap-4
          md:grid-cols-2 lg:grid-cols-3
          ${add_bottom_border ? "pb-8 border-b border-gray-200" : ""}
        `}
      >
        {items.map((item, index) => renderItem(item, index))}
      </div>
    </>
  );

  if (add_background) {
    return (
      <section className="w-screen relative left-1/2 right-1/2 -mx-[50vw] bg-gradient-to-b from-[rgba(237,237,249,0)] to-[#DEE5E8] py-12">
        <div
          className="absolute left-0 bottom-0 w-[300px] h-[300px] bg-no-repeat bg-left-bottom bg-contain pointer-events-none"
          style={{
            backgroundImage: "url(/images/left-corner-supergrafis.png)",
          }}
        />
        <div className="container max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          {content}
        </div>
      </section>
    );
  }

  return <div className="w-full">{content}</div>;
}
